import { useCallback, useMemo, useState } from 'react';

/** Seleção múltipla por `_id` (página atual). */
export function useSelection<T extends { _id: string }>(items: T[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const selected = useMemo(
    () => items.filter((item) => selectedIds.has(item._id)),
    [items, selectedIds],
  );

  const allOnPageSelected = items.length > 0 && items.every((item) => selectedIds.has(item._id));
  const someOnPageSelected = items.some((item) => selectedIds.has(item._id));

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const item of items) next.delete(item._id);
      } else {
        for (const item of items) next.add(item._id);
      }
      return next;
    });
  }, [allOnPageSelected, items]);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const setIds = useCallback((ids: Iterable<string>) => {
    setSelectedIds(new Set(ids));
  }, []);

  return {
    selectedIds,
    selected,
    count: selectedIds.size,
    allOnPageSelected,
    someOnPageSelected,
    toggle,
    toggleAllOnPage,
    clear,
    setIds,
  };
}
